import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Action } from '../../../actions';
import {
    UpdateInternalBannerTemplateResources,
    updateInternalBannerTemplateResourcesAction
} from '../../../actions/bannersEdit/internal/updateInternalBannerTemplateResources';
import {
    ShowInternalBannerImageUploadError,
    showInternalBannerImageUploadErrorAction
} from '../../../actions/imageEditor/internal/showInternalBannerImageUploadError';
import {
    UpdateInternalBannerImage,
    updateInternalBannerImageAction
} from '../../../actions/imageEditor/internal/updateInternalBannerImage';
import {
    UPLOAD_INTERNAL_BANNER_IMAGE,
    UploadInternalBannerImage
} from '../../../actions/imageEditor/internal/uploadInternalBannerImage';
import { getTextBannerImageFromUploadResponse } from '../../../mappers/banners-edit/api-data-mappers';
import { selectEditedBanner } from '../../../selectors/bannerEdit/banner';
import { internalImageUploadErrorMapper } from '../../../service/BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import { ApiMethodExecutingError } from '../../../service/Errors';
import { getImageEditorIdByBannerId } from '../../../service/ImageHelpers/ImageHelpers';
import { getUploadingImagesHash } from '../../../service/ImageHelpers/ImageHelpers';
import { EpicDeps } from '../../../service/StoreFactory';
import { getBannerWithImage, MassActionId } from '../../../typings/bannersEditState';
import { AdvType, BannerImageType } from '../../../typings/domain';
import { State } from '../../../typings/state';

type EpicActions =
    | ShowInternalBannerImageUploadError
    | UpdateInternalBannerImage
    | UpdateInternalBannerTemplateResources;

export const uploadInternalBannerImageEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPLOAD_INTERNAL_BANNER_IMAGE),
        mergeMap(
            (action: UploadInternalBannerImage): Observable<EpicActions> => {
                const state = store$.value;
                const { bannerId, resourceId, filesToUpload } = action;

                if (bannerId === MassActionId.bannersEdit) {
                    return EMPTY;
                }

                const banner = getBannerWithImage(selectEditedBanner(state, bannerId));

                if (!banner) {
                    return EMPTY;
                }

                const imageEditorId = getImageEditorIdByBannerId(bannerId, resourceId);
                const { uploadingData: imagesHash } = state.imageEditor[imageEditorId].image;

                return from(
                    deps.restApiManager.uploadAdBannerImage(state.commonData.login, AdvType.INTERNAL, filesToUpload)
                ).pipe(
                    mergeMap(response => {
                        if (imagesHash === getUploadingImagesHash(filesToUpload)) {
                            const image = getTextBannerImageFromUploadResponse(
                                response.result[0],
                                BannerImageType.INTERNAL
                            );

                            return of(
                                updateInternalBannerImageAction(bannerId, resourceId, image),
                                updateInternalBannerTemplateResourcesAction(bannerId, resourceId, image.hash)
                            );
                        }

                        return EMPTY;
                    }),
                    catchError(
                        (error: ApiMethodExecutingError): Observable<ShowInternalBannerImageUploadError> => {
                            if (imagesHash === getUploadingImagesHash(filesToUpload)) {
                                return of(
                                    showInternalBannerImageUploadErrorAction(
                                        bannerId,
                                        resourceId,
                                        error.getErrors().map(err => ({
                                            code: err.code,
                                            text: internalImageUploadErrorMapper(err)
                                        }))
                                    )
                                );
                            }

                            return EMPTY;
                        }
                    )
                );
            }
        )
    );
};
